import React, {useEffect, useState} from 'react'
import {
  Alert,
  Grid,
  FormControlLabel,
  FormHelperText,
  Select,
  MenuItem,
  CircularProgress,
  FormControl,
} from '@mui/material'
import {AUDIENCE} from '../../../utils/constants'
import emailTemplateApi from '../../../services/api/emailTemplate/emailTemplateApi'
import checkinApi from '../../../services/api/checkin/checkinApi'
import {getApiHookParams} from '../../../auth'
import useCustomerData from '../../../hooks/useCustomerData'
import {TextField} from '@mui/material'
import {AdapterDayjs} from '@mui/x-date-pickers/AdapterDayjs'
import {LocalizationProvider} from '@mui/x-date-pickers/LocalizationProvider'
import WbSwitch from '../../../components/common/WbSwitch'
import InputLabel from '@mui/material/InputLabel'
import {TimePicker} from '@mui/x-date-pickers/TimePicker'
import dayjs from 'dayjs'
import DOMPurify from 'dompurify'
import {buildTemplateOptions, missingEmailWarning} from '../checkInWizardRules'

export default function SelectEmailTemplate({
  emailTemplate,
  setEmailTemplate,
  startHour,
  setStartHour,
  startMinute,
  setStartMinute,
  useEmail,
  setUseEmail,
  participantFilter,
}) {
  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)
  const [inviteReadiness, setInviteReadiness] = useState(null)

  const apiParams = getApiHookParams()
  const {isMat, groupId, schoolId} = apiParams
  // Only the preview needs the customer's display name; the real placeholders are filled when
  // the email is sent, so a missing name here costs nothing but the preview text.
  const customerName = useCustomerData()?.name || ''

  useEffect(() => {
    setLoading(true)
    emailTemplateApi
      .get(
        apiParams.isMat ? 0 : apiParams.schoolId,
        apiParams.isMat ? apiParams.groupId : 0,
        AUDIENCE.STUDENT,
        0
      )
      .then(e => {
        setTemplates(buildTemplateOptions(emailTemplate, e))
      })
      .catch(() => setTemplates(buildTemplateOptions(emailTemplate, [])))
      .finally(() => setLoading(false))
  }, [])

  // How many of the chosen pupils can actually be emailed is only worth asking
  // once the school has said it wants to email them.
  useEffect(() => {
    if (!useEmail) {
      setInviteReadiness(null)
      return
    }

    checkinApi
      .getInviteReadiness({
        matId: isMat ? groupId : 0,
        schoolId: isMat ? 0 : schoolId,
        participationFilter: participantFilter,
      })
      .then(setInviteReadiness)
      .catch(error => {
        // A missing warning is better than a wrong one, so a failed check stays quiet.
        console.error('Failed to check which pupils can be emailed', error)
        setInviteReadiness(null)
      })
  }, [useEmail, participantFilter, isMat, groupId, schoolId])

  const uninvitablePupilsWarning = missingEmailWarning(inviteReadiness)

  if (loading) return <CircularProgress />
  return (
    <>
      <Grid container>
        <Grid item lg={12} md={12} sm={12} xs={12} mt={4}>
          <h3>
            <strong>Student Invitation</strong>
          </h3>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12} mt={1}>
          <FormControlLabel
            // onChange={e => setFrequency(e.target.value)}
            value={1}
            control={
              <WbSwitch
                size="small"
                checked={useEmail}
                onChange={e => setUseEmail(!useEmail)}
              />
            }
            label="Email Student"
          />
        </Grid>

        {useEmail && (
          <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
            <Grid container spacing={2} mt={1}>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <FormControl fullWidth>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale="en-gb"                  
                >
                  <TimePicker
                    renderInput={props => <TextField {...props} />}                    
                    label="Send email at"
                    value={dayjs(
                      `2022-01-01T${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}`
                    )}
                    onChange={newValue => {
                      setStartHour(newValue.$H)
                      setStartMinute(newValue.$m)
                    }}
                  />
                </LocalizationProvider>
                <FormHelperText>
                  Emails are sent at this time on the day each check-in
                  launches. Check-ins themselves open at 08:00.
                </FormHelperText>
                </FormControl>
              </Grid>
              <Grid item lg={4} md={6} sm={12} xs={12}>
                <FormControl fullWidth>
                  <InputLabel id="template-select">Email Template</InputLabel>
                  <Select
                    label="Email Template"
                    labelId="template-select"
                    onChange={e => setEmailTemplate(e.target.value)}
                    fullWidth
                    value={emailTemplate || ''}
                    disabled={loading || templates.length === 0}
                  >
                    {templates.map(t => (
                      <MenuItem key={t.id} value={t}>
                        {t.subject}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {uninvitablePupilsWarning && (
              <Grid container mt={2}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <Alert severity="warning">{uninvitablePupilsWarning}</Alert>
                </Grid>
              </Grid>
            )}

            <div className='checkin-email-preview'>
              <Grid container mt={2}>
                <Grid item lg={12} md={12} sm={12} xs={12}>
                  <strong>Subject:</strong> {emailTemplate && emailTemplate.subject}
                </Grid>
                <Grid item lg={12} md={12} sm={12} xs={12} mt={2}>
                  <strong>Message:</strong>
                  {emailTemplate && (
                    <div
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          emailTemplate.message
                            ?.replace('{schoolName}', customerName)
                            ?.replace('{school}', customerName) ?? ''
                        ),
                      }}
                      style={{height: '250px', overflow: 'auto'}}
                    ></div>
                  )}
                </Grid>
              </Grid>
            </div>
          </Grid>
        )}
      </Grid>
    </>
  )
}
